/**
 * One player, or one partnership — FR-STAT-5, FR-STAT-6, FR-STAT-7.
 *
 * "Who do I win with, who do I lose to, and is that real or six games?" The
 * club has asked that of a spreadsheet for two years.
 *
 * Every record carries its sample, and a record below BR-PAIR-4's minimum is
 * shown flagged rather than hidden. A hidden row reads as "no data"; a flagged
 * one reads as "not yet", and those are different answers. The lists are
 * ordered by games played rather than by win rate, because sorting by rate
 * would put a single 1-0 above a 9-5 — which is the misreading the minimum
 * exists to prevent.
 */

import { useEffect, useState } from "react";
import {
  api,
  type CounterpartRecord,
  type PairProfileReport,
  type PlayerProfileReport,
} from "../api.js";

export function PlayerProfile({
  playerId,
  onClose,
  onOpenPair,
}: {
  playerId: string;
  /**
   * Omitted when the profile *is* the screen rather than something opened on
   * top of one — the member's own stats on their own page have nowhere to go
   * back to, and a Back button that does nothing is the defect this project
   * keeps rediscovering.
   */
  onClose?: () => void;
  onOpenPair?: (a: string, b: string) => void;
}): React.JSX.Element {
  const [gameType, setGameType] = useState<"doubles" | "singles">("doubles");
  const [profile, setProfile] = useState<PlayerProfileReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setProfile(null);
    api
      .playerProfile(playerId, gameType)
      .then(setProfile)
      .catch((e: unknown) =>
        setError(e instanceof Error ? e.message : "Could not load that profile."),
      );
  }, [playerId, gameType]);

  if (error) {
    return (
      <>
        <p className="error">{error}</p>
        {onClose ? (
          <button className="quiet" onClick={onClose}>
            Back
          </button>
        ) : null}
      </>
    );
  }

  if (!profile) return <p className="muted small">Loading…</p>;

  return (
    <>
      <div className="row space-between">
        <div className="brand">
          <h1 className="display">{profile.displayName}</h1>
          <div className="meta">
            {profile.allTime.games} {profile.allTime.games === 1 ? "game" : "games"} ·{" "}
            {profile.sessionsPlayed} {profile.sessionsPlayed === 1 ? "session" : "sessions"}
          </div>
        </div>
        {onClose ? (
          <button className="mini" onClick={onClose}>
            Back
          </button>
        ) : null}
      </div>

      {/* BR-STAT-3 — a switch, never a total. */}
      <div className="row" style={{ margin: ".6rem 0" }}>
        {(["doubles", "singles"] as const).map((t) => (
          <button
            key={t}
            className={t === gameType ? "chip chip-on" : "chip"}
            onClick={() => setGameType(t)}
          >
            {t}
          </button>
        ))}
      </div>

      {profile.allTime.games === 0 ? (
        <section className="block">
          <h3 className="card-head">Nothing yet</h3>
          <p>
            No committed {gameType} games. Figures appear here once a session
            they played in has been finished.
          </p>
        </section>
      ) : (
        <>
          {profile.insights.length > 0 ? (
            <section className="block">
              <h3 className="card-head">What the games say</h3>
              {profile.insights.map((insight) => (
                <p key={insight.kind} className="insight">
                  {insight.text}
                  <span className="muted small"> ({insight.sample} games)</span>
                </p>
              ))}
            </section>
          ) : null}

          <section className="block">
            <h3 className="card-head">
              Overall
              <span className="muted small">
                recent = last {profile.recentSessions}{" "}
                {profile.recentSessions === 1 ? "session" : "sessions"}
              </span>
            </h3>
            <div className="table-scroll">
              <table className="standings">
                <thead>
                  <tr>
                    <th scope="col" className="sticky-col">
                      &nbsp;
                    </th>
                    <th scope="col">G</th>
                    <th scope="col">W</th>
                    <th scope="col">Win%</th>
                    <th scope="col">Dom</th>
                    <th scope="col">MP</th>
                  </tr>
                </thead>
                <tbody>
                  {(
                    [
                      ["All time", profile.allTime],
                      ["Recent", profile.recent],
                    ] as const
                  ).map(([label, s]) => (
                    <tr key={label}>
                      <th scope="row" className="sticky-col">
                        {label}
                      </th>
                      <td>{s.games}</td>
                      <td>{s.wins}</td>
                      <td>{pct(s.winPct)}</td>
                      <td>{dec(s.dominance)}</td>
                      <td className="strong">{s.matchPoints}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <Counterparts
            title="Playing with"
            note="Games on the same side."
            rows={profile.partners}
            onOpen={onOpenPair ? (id) => onOpenPair(profile.playerId, id) : undefined}
          />
          <Counterparts
            title="Playing against"
            note="Games on opposing sides, whoever either of you partnered."
            rows={profile.opponents}
          />
        </>
      )}
    </>
  );
}

function Counterparts({
  title,
  note,
  rows,
  onOpen,
}: {
  title: string;
  note: string;
  rows: CounterpartRecord[];
  onOpen?: (playerId: string) => void;
}): React.JSX.Element | null {
  if (rows.length === 0) return null;
  const minimum = rows[0].minimumGames;

  return (
    <section className="block">
      <h3 className="card-head">{title}</h3>
      <div className="table-scroll">
        <table className="standings">
          <thead>
            <tr>
              <th scope="col" className="sticky-col">
                Player
              </th>
              <th scope="col">G</th>
              <th scope="col">W</th>
              <th scope="col">Win%</th>
              <th scope="col">Dom</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.playerId}>
                <th scope="row" className="sticky-col">
                  {onOpen ? (
                    <button className="linkish" onClick={() => onOpen(r.playerId)}>
                      {r.displayName}
                    </button>
                  ) : (
                    r.displayName
                  )}
                  {/* Shown, flagged, never hidden — BR-PAIR-4. */}
                  {r.meetsMinimum ? null : (
                    <span className="provisional-flag">
                      {r.games} of {r.minimumGames}
                    </span>
                  )}
                </th>
                <td>{r.games}</td>
                <td>{r.wins}</td>
                <td>{pct(r.winPct)}</td>
                <td className={sign(r.dominance)}>{dec(r.dominance)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="muted small">
        {note} A record needs {minimum} games before it counts as one; the rest
        are shown anyway, marked.
      </p>
    </section>
  );
}

/** BR-STAT-1 — an em dash where there is nothing to divide by. */
function pct(value: number | null): string {
  return value === null ? "—" : `${(value * 100).toFixed(0)}%`;
}

function dec(value: number | null): string {
  return value === null ? "—" : value.toFixed(2);
}

function sign(value: number | null): string | undefined {
  if (value === null || value === 0) return undefined;
  return value > 0 ? "pos" : "neg";
}

/**
 * One partnership — FR-STAT-6.
 *
 * BR-PAIR-2 for the pair's own record (games on the same side), BR-PAIR-3 for
 * who they have faced (opposing sides). Reached by tapping a partner's name on
 * a player profile, which is the moment the question arises.
 */
export function PairProfile({
  playerA,
  playerB,
  onClose,
}: {
  playerA: string;
  playerB: string;
  onClose: () => void;
}): React.JSX.Element {
  const [pair, setPair] = useState<PairProfileReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setPair(null);
    api
      .pairProfile(playerA, playerB)
      .then(setPair)
      .catch((e: unknown) =>
        setError(e instanceof Error ? e.message : "Could not load that partnership."),
      );
  }, [playerA, playerB]);

  if (error) {
    return (
      <>
        <p className="error">{error}</p>
        <button className="quiet" onClick={onClose}>
          Back
        </button>
      </>
    );
  }

  if (!pair) return <p className="muted small">Loading…</p>;

  const t = pair.together;
  return (
    <>
      <div className="row space-between">
        <div className="brand">
          <h1 className="display">{pair.label}</h1>
          <div className="meta">
            {t.games} {t.games === 1 ? "game" : "games"} together
          </div>
        </div>
        <button className="mini" onClick={onClose}>
          Back
        </button>
      </div>

      <section className="block">
        <h3 className="card-head">Together</h3>
        {/* BR-PAIR-4 — said plainly rather than by hiding the row. */}
        {pair.meetsMinimum ? null : (
          <p className="muted small">
            {t.games} of {pair.minimumGames} games — not yet enough to call this
            a record, but the figures are real.
          </p>
        )}
        <div className="table-scroll">
          <table className="standings">
            <thead>
              <tr>
                <th scope="col" className="sticky-col">
                  &nbsp;
                </th>
                <th scope="col">G</th>
                <th scope="col">W</th>
                <th scope="col">Win%</th>
                <th scope="col">PW</th>
                <th scope="col">PL</th>
                <th scope="col">Dom</th>
                <th scope="col">MP</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <th scope="row" className="sticky-col">
                  {pair.label}
                </th>
                <td>{t.games}</td>
                <td>{t.wins}</td>
                <td>{pct(t.winPct)}</td>
                <td>{t.pointsWon}</td>
                <td>{t.pointsLost}</td>
                <td className={sign(t.dominance)}>{dec(t.dominance)}</td>
                <td className="strong">{t.matchPoints}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <Counterparts
        title="Against"
        note="Games where this pair faced them, whoever they were partnered with."
        rows={pair.against}
      />
    </>
  );
}
